import dayjs from "dayjs";
import { useContext, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import MyTitle from "../../../../components/MyTitle";
import { useBoardItemComment } from "../../../../hooks/useBoardItemComment";
import MyLoading from "../../../../components/MyLoading";
import FileItem from "../../../../components/inputs/MyInputFile/FileItem";
import { AuthContext } from "../../../../context/AuthProvider";
import DeleteBoardItemCommentDialog from "../../../Common/DeleteBoardItemCommentDialog";
import { PopupContext } from "../../../../context/PopupProvider";
import MyButton from "../../../../components/MyButton";
import { renderBody } from "../../../../utils";
import ResearchCommentForm04 from "./ResearchCommentForm04";

const ResearchCommentList04 = ({ board, boardItem }) => {
  const { useGetBoardItemComments } = useBoardItemComment();
  const { isAdmin, isAuthenticated, profile } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { openPopup } = useContext(PopupContext);

  const [selectComment, setSelectComment] = useState(null);

  const { data, isLoading } = useGetBoardItemComments(
    {
      size: 10000,
      page: 1,
      sort: "createdDate,Asc",
      boardCode: board?.boardCode,
      itemNo: boardItem?.itemNo,
      useComment: board?.options?.useComment,
    },
    (data) => data.data
  );

  const openPopupDelete = (commentNo) =>
    openPopup(
      <DeleteBoardItemCommentDialog
        mode="reply"
        boardCode={board?.boardCode}
        itemNo={boardItem?.itemNo}
        commentNo={commentNo}
        deleteMessage="답변이 삭제되었습니다."
        onRedirect={() => {
          queryClient.invalidateQueries({
            queryKey: ["/board-item/{boardCode}/{itemNo}"],
          });
        }}
      />
    );

  return isLoading ? (
    <MyLoading className="h-[150px]" />
  ) : (
    <>
      {data?.content?.length > 0 ? (
        <div>
          <MyTitle title="답변 내용" className="mb-4" />
          <div className="overflow-x-auto max-w-full">
            {data.content.map((comment) => (
              <div key={comment.commentNo} className="mb-10">
                {selectComment &&
                selectComment.commentNo === comment.commentNo ? (
                  <ResearchCommentForm04
                    board={board}
                    boardItem={boardItem}
                    selectComment={selectComment}
                    setSelectComment={setSelectComment}
                  />
                ) : (
                  <>
                    <table
                      className={`w-full border-collapse mb-3 border-t-2 border-primary ${
                        comment.hidden ? "hidden" : ""
                      }`}
                    >
                      <tbody>
                        <tr className="md:table-row hidden">
                          <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%]">
                            작성자
                          </th>
                          <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-[25%]">
                            {comment?.writer?.name}
                          </td>
                          <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm w-[25%]">
                            작성일
                          </th>
                          <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-[25%]">
                            {dayjs(comment?.createdDate).format("YYYY-MM-DD")}
                          </td>
                        </tr>
                        <tr className="md:hidden">
                          <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm w-[100px]">
                            작성자
                          </th>
                          <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                            {comment?.writer?.name}
                          </td>
                        </tr>
                        <tr className="md:hidden">
                          <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm w-[100px]">
                            작성일
                          </th>
                          <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                            {dayjs(comment?.createdDate).format("YYYY-MM-DD")}
                          </td>
                        </tr>

                        <tr>
                          <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                            답변내용
                          </th>
                          <td
                            className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto"
                            colSpan={3}
                          >
                            {comment?.comment ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: renderBody(
                                    comment.comment,
                                    comment.embeddedFiles
                                  ),
                                }}
                                className="body-text"
                              />
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                            첨부파일
                          </th>
                          <td
                            className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto"
                            colSpan={3}
                          >
                            {comment?.attachedFiles?.length > 0 ? (
                              <>
                                {comment.attachedFiles.map((c) => (
                                  <FileItem key={c.fileId} file={c} />
                                ))}
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {isAdmin ||
                    (isAuthenticated &&
                      profile?.userId === comment?.writer?.userId) ? (
                      <div className="flex items-center justify-end sm:gap-4 gap-2">
                        <MyButton
                          variant="outlined"
                          className="sm:min-w-[120px]"
                          onClick={() => openPopupDelete(comment.commentNo)}
                        >
                          <span className="font-semibold">삭제</span>
                        </MyButton>
                        <MyButton
                          variant="contained"
                          color="primary"
                          className="sm:min-w-[120px]"
                          onClick={() => setSelectComment(comment)}
                        >
                          답변 수정하기
                        </MyButton>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          style={{
            marginTop: "-40px",
          }}
        />
      )}
    </>
  );
};

export default ResearchCommentList04;
